import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, MapPin, RefreshCw, Bot } from "lucide-react";

export function DecisionZeroPanel({ gameId, teams, onRefresh }) {
  const { t } = useTranslation();
  const [hubNames, setHubNames] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/games/${gameId}/hubs`)
      .then(res => {
        const map = {};
        (res.data || []).forEach(h => { map[h.code] = h.name; });
        setHubNames(map);
      })
      .catch(() => {});
  }, [gameId]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  // Live view: poll while teams are still picking
  useEffect(() => {
    const id = setInterval(() => { onRefresh(); }, 15000);
    return () => clearInterval(id);
  }, [onRefresh]);

  const humanTeams = teams.filter(tm => (tm.members?.length || 0) > 0);
  const aiTeams = teams.filter(tm => (tm.members?.length || 0) === 0);
  const chosen = humanTeams.filter(tm => tm.hub_id).length;
  const pct = humanTeams.length > 0 ? Math.round((chosen / humanTeams.length) * 100) : 0;

  return (
    <Card className="border-amber-200" data-testid="decision-zero-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <MapPin className="w-4 h-4 text-amber-600" />
              {t("instructor.decisionZeroTitle")}
            </CardTitle>
            <CardDescription>{t("instructor.decisionZeroHint")}</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-mono font-semibold text-slate-700" data-testid="decision-zero-progress">
              {chosen} / {humanTeams.length}
            </span>
            <Button variant="outline" size="sm" className="gap-2" onClick={refresh} disabled={refreshing} data-testid="decision-zero-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {t("common.refresh")}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${pct}%` }}
            data-testid="decision-zero-bar"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {humanTeams.map(tm => (
            <div
              key={tm.id}
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
                tm.hub_id ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"
              }`}
              data-testid={`decision-zero-team-${tm.id}`}
            >
              <div className="min-w-0">
                <p className="font-medium text-slate-900 truncate">{tm.name}</p>
                <p className="text-xs text-slate-500">{tm.members?.length || 0} {t("instructor.studentsShort")}</p>
              </div>
              {tm.hub_id ? (
                <span className="flex items-center gap-1.5 text-sm text-emerald-700 shrink-0">
                  <CheckCircle2 className="w-4 h-4" />
                  {hubNames[tm.hub_id] || tm.hub_id}
                  <span className="font-mono text-xs text-emerald-600">{tm.hub_id}</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-sm text-amber-700 shrink-0">
                  <Clock className="w-4 h-4" />
                  {t("instructor.hubPending")}
                </span>
              )}
            </div>
          ))}
        </div>

        {aiTeams.length > 0 && (
          <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="decision-zero-ai-note">
            <Bot className="w-3.5 h-3.5" />
            {t("instructor.aiHubNote", { count: aiTeams.length })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
